import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// Lazy-load DevTool only in development to avoid bundling devDependency in production
const DevTool =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@hookform/devtools').then((module) => ({
          default: module.DevTool,
        })),
      )
    : () => null;

import { FormProvider, useForm } from 'react-hook-form';

/**
 * @typedef {import('../../../types/provider').Provider} Provider
 * @typedef {import('../../../types/location').LocationOption} LocationOption
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

import { useTrackFormSubmitted } from '../../../analytics/cohesion';
import { getProviderBlockerMessage } from '../validation';
import { FIELDS } from './constants';
import EmailField from './fields/EmailField';
import Last4Field from './fields/Last4Field';
import LocationField from './fields/LocationField';
import NameField from './fields/NameField';
import NpiField from './fields/NpiField';
import ProviderSearchResult from './ProviderSearchResult';

// Cohesion tracking constants
const FORM_TRACKING = {
  formName: 'Provider Search',
  formType: 'Search',
  formId: 'provider-search-form',
};

const DEFAULT_VALUES = Object.freeze({
  [FIELDS.npi]: '',
  [FIELDS.locationCity]: '',
  [FIELDS.name]: '',
  [FIELDS.last4]: '',
  [FIELDS.verifiedLast4]: '',
  [FIELDS.email]: '',
});

/**
 * Error banner for displaying validation/limit messages.
 * @param {{ children: React.ReactNode }} props
 */

/**
 * Allows users to search for a provider, validate their identity, and add to roster.
 *
 * Flow:
 * 1. User searches by NPI or Location+Name
 * 2. If provider is "good" (eligible + unique), Last4 and Email fields enable
 * 3. User verifies identity with Last4 and provides email
 * 4. User clicks Add to add provider to roster
 *
 * @param {Object} props
 * @param {function(RosterProvider): void} props.onAdd
 * @param {boolean} props.disabled
 * @param {Array<RosterProvider>} props.existingProviders - Providers in current session
 * @param {Array<{providerCode: string}>} props.existingRosterProviders - Providers already in user's roster (from backend)
 * @param {function(): void} [props.onSearchFieldsChange] - Callback when search fields change (to clear CSV errors)
 */
const ProviderSearch = ({
  onAdd,
  disabled = false,
  existingProviders = [],
  existingRosterProviders = [],
  onSearchFieldsChange,
}) => {
  // ─────────────────────────────────────────────────────────────────────────
  // Form state via React Hook Form
  // ─────────────────────────────────────────────────────────────────────────
  const methods = useForm({
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    watch,
    getValues,
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  // Watch form values (RHF is the source of truth)
  const formValues = watch() || {};
  const {
    [FIELDS.locationCity]: locationCityValue,
    [FIELDS.verifiedLast4]: verifiedLast4Value,
    [FIELDS.email]: emailValue,
  } = formValues;

  // ─────────────────────────────────────────────────────────────────────────
  // Provider search state
  // ─────────────────────────────────────────────────────────────────────────
  const [foundProvider, setFoundProvider] = useState(
    /** @type {Provider|null} */ (null),
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Cohesion tracking
  // ─────────────────────────────────────────────────────────────────────────
  const trackFormSubmitted = useTrackFormSubmitted();

  // Shared form context for all provider search tracking events
  const formContext = useMemo(
    () => ({
      formName: FORM_TRACKING.formName,
      formType: FORM_TRACKING.formType,
      formId: FORM_TRACKING.formId,
    }),
    [],
  );

  // Helper to create a single-field tracking event
  // Each call creates a completely separate event to avoid nesting/batching
  const trackSingleFieldSearch = useCallback(
    /**
     * @param {string} fieldName - Field name (e.g., 'Location', 'Name', 'NPI')
     * @param {string} fieldValue - Field value to track
     * @param {string} fieldId - Field ID for tracking (e.g., 'provider-search-location')
     */
    (fieldName, fieldValue, fieldId) => {
      trackFormSubmitted({
        formContext,
        field: [
          {
            fieldName,
            fieldType: 'text',
            fieldValue,
            fieldId,
          },
        ],
      });
    },
    [trackFormSubmitted, formContext],
  );

  // Separate tracking functions to ensure completely independent events
  // Location search tracking - fires when location is selected
  const trackLocationSearch = useCallback(
    (locationValue) => {
      trackSingleFieldSearch(
        'Location',
        locationValue,
        'provider-search-location',
      );
    },
    [trackSingleFieldSearch],
  );

  // Name search tracking - fires when name search finds a provider
  const trackNameSearch = useCallback(
    (nameValue) => {
      trackSingleFieldSearch('Name', nameValue, 'provider-search-name');
    },
    [trackSingleFieldSearch],
  );

  // NPI search tracking - fires when NPI search finds a provider
  const trackNpiSearch = useCallback(
    (npiValue) => {
      trackSingleFieldSearch('NPI', npiValue, 'provider-search-npi');
    },
    [trackSingleFieldSearch],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Derived state using pure validation functions
  // ─────────────────────────────────────────────────────────────────────────
  /**
   * Merges session providers (current checkout session) and existing roster providers
   * (from backend) to create a comprehensive list for duplicate checking.
   *
   * This ensures we prevent adding:
   * 1. Providers already in the current session (existingProviders)
   * 2. Providers already in the user's roster (existingRosterProviders)
   *
   * @type {Array<{providerCode: string}>}
   */
  const allExistingProviders = useMemo(() => {
    const sessionProviderCodes = new Set(
      existingProviders.map((p) => p.providerCode).filter(Boolean),
    );
    const rosterProviderCodes = new Set(
      existingRosterProviders.map((p) => p.providerCode).filter(Boolean),
    );

    // Combine both sets to get all unique provider codes
    const allCodes = new Set([...sessionProviderCodes, ...rosterProviderCodes]);

    // Return array with providerCode for validation
    return Array.from(allCodes).map((code) => ({ providerCode: code }));
  }, [existingProviders, existingRosterProviders]);

  const blockerMessage = getProviderBlockerMessage(
    foundProvider,
    allExistingProviders,
  );

  const providerGood = !!foundProvider && !blockerMessage;

  // verifiedLast4 is set by Last4Field only when API confirms validity
  const validLast4 = Boolean(verifiedLast4Value);
  const validEmail = Boolean(emailValue) && !errors[FIELDS.email];

  // Can add when: provider is good + last4 verified + email valid
  const canAdd = providerGood && isValid;

  const fieldsDisabled = disabled;

  // Build RosterProvider when all criteria met
  const rosterProvider = useMemo(() => {
    if (!foundProvider || !canAdd) return null;

    return /** @type {RosterProvider} */ ({
      ...foundProvider,
      validEmail,
      validLast4,
      email: emailValue,
      last4: verifiedLast4Value,
      emailFromApi: foundProvider?.email ?? foundProvider?.Email ?? emailValue,
    });
  }, [
    foundProvider,
    canAdd,
    emailValue,
    verifiedLast4Value,
    validEmail,
    validLast4,
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // Field reset helpers
  // ─────────────────────────────────────────────────────────────────────────
  const resetFieldToDefault = useCallback(
    /** @param {string} fieldName */
    (fieldName) => {
      setValue(fieldName, DEFAULT_VALUES[fieldName], {
        shouldValidate: true,
        shouldDirty: false,
      });
    },
    [setValue],
  );

  const resetVerificationFields = useCallback(() => {
    resetFieldToDefault(FIELDS.last4);
    resetFieldToDefault(FIELDS.verifiedLast4);
    resetFieldToDefault(FIELDS.email);
  }, [resetFieldToDefault]);

  /**
   * Pre-populate email when search API returns it (handles both camelCase and PascalCase).
   * @param {Provider|null} provider - Provider from search API
   */
  const prePopulateEmailFromProvider = useCallback(
    (provider) => {
      const emailFromApi = provider?.email ?? provider?.Email;
      if (emailFromApi && typeof emailFromApi === 'string') {
        setValue(FIELDS.email, emailFromApi.trim(), {
          shouldValidate: true,
          shouldDirty: false,
        });
      }
    },
    [setValue],
  );

  const resetAllFields = useCallback(() => {
    Object.keys(DEFAULT_VALUES).forEach(resetFieldToDefault);
    setFoundProvider(null);
    resetVerificationFields();
  }, [resetFieldToDefault, resetVerificationFields]);

  // ─────────────────────────────────────────────────────────────────────────
  // Field handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleNpiUpdatedProvider = useCallback(
    /**
     * When provider changes (from NPI or Name search), reset verification fields
     * @param {Provider|null} provider
     */
    (provider) => {
      const prevProvider = foundProvider;
      setFoundProvider(provider);
      resetVerificationFields();

      // Notify parent that search fields changed (to clear CSV errors)
      if (onSearchFieldsChange) {
        onSearchFieldsChange();
      }

      if (!provider) return;

      prePopulateEmailFromProvider(provider);
      resetFieldToDefault(FIELDS.locationCity);
      resetFieldToDefault(FIELDS.name);

      // Pre-populate email when search API returns it
      const emailFromApi = provider.email ?? provider.Email;
      if (emailFromApi && typeof emailFromApi === 'string') {
        setValue(FIELDS.email, emailFromApi.trim(), {
          shouldValidate: true,
          shouldDirty: false,
        });
      }

      if (
        !prevProvider ||
        prevProvider.providerCode !== provider.providerCode
      ) {
        const values = getValues();

        if (values[FIELDS.npi]) {
          trackNpiSearch(values[FIELDS.npi]);
        }
      }
    },
    [
      foundProvider,
      resetVerificationFields,
      resetFieldToDefault,
      setValue,
      prePopulateEmailFromProvider,
      getValues,
      trackNpiSearch,
      onSearchFieldsChange,
    ],
  );

  const handleNameUpdatedProvider = useCallback(
    /**
     * When provider changes (from NPI or Name search), reset verification fields
     * @param {Provider|null} provider
     */
    (provider) => {
      const prevProvider = foundProvider;
      setFoundProvider(provider);
      resetVerificationFields();

      // Notify parent that search fields changed (to clear CSV errors)
      if (onSearchFieldsChange) {
        onSearchFieldsChange();
      }

      if (!provider) return;

      prePopulateEmailFromProvider(provider);
      resetFieldToDefault(FIELDS.npi);

      // Pre-populate email when search API returns it
      const emailFromApi = provider.email ?? provider.Email;
      if (emailFromApi && typeof emailFromApi === 'string') {
        setValue(FIELDS.email, emailFromApi.trim(), {
          shouldValidate: true,
          shouldDirty: false,
        });
      }

      if (
        !prevProvider ||
        prevProvider.providerCode !== provider.providerCode
      ) {
        const values = getValues();

        // Only track the Name field - Location is tracked independently when selected
        if (values[FIELDS.name]) {
          trackNameSearch(values[FIELDS.name]);
        }
      }
    },
    [
      foundProvider,
      resetVerificationFields,
      resetFieldToDefault,
      setValue,
      prePopulateEmailFromProvider,
      getValues,
      trackNameSearch,
      onSearchFieldsChange,
    ],
  );

  const handleLocationChange = useCallback(
    /**
     * When location changes, reset name field and any found provider
     * @param {LocationOption|null} location
     */
    (location) => {
      resetFieldToDefault(FIELDS.name);
      resetFieldToDefault(FIELDS.npi);
      setFoundProvider(null);
      resetVerificationFields();

      // Notify parent that search fields changed (to clear CSV errors)
      if (onSearchFieldsChange) {
        onSearchFieldsChange();
      }

      // Track location selection independently - separate event
      if (location?.city) {
        trackLocationSearch(location.city);
      }
    },
    [
      resetFieldToDefault,
      resetVerificationFields,
      trackLocationSearch,
      onSearchFieldsChange,
    ],
  );

  /**
   * Add provider to roster after validating all fields
   */
  const handleAdd = useCallback(() => {
    const onSubmitSuccess = () => {
      if (!rosterProvider) return;

      onAdd(rosterProvider);
      resetAllFields();
    };

    handleSubmit(onSubmitSuccess)();
  }, [handleSubmit, onAdd, rosterProvider, resetAllFields]);

  // ─────────────────────────────────────────────────────────────────────────
  // Disable verification fields until we have a "good" provider
  // ─────────────────────────────────────────────────────────────────────────
  const disableVerificationFields = !providerGood || fieldsDisabled;

  // Get provider code for Last4 validation
  const providerCode = foundProvider?.providerCode?.trim() || '';

  // Only count errors that have actual error messages
  const hasAnyErrorMessages = Object.values(errors || {}).some(
    (error) => error?.message && String(error.message).trim().length > 0,
  );

  return (
    <div className="flex flex-col gap-6 w-full border-y border-gray-20 py-6">
      <FormProvider {...methods}>
        <div className="w-full" data-qa-target={FORM_TRACKING.formId}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_1fr_1fr_1fr] gap-2 lg:gap-x-4 lg:gap-y-0">
            {/* NPI Field */}
            <NpiField
              onProviderUpdate={handleNpiUpdatedProvider}
              disabled={fieldsDisabled}
            />

            {/* OR Separator */}
            <div className="flex flex-col">
              <div className="lg:h-14 lg:pb-2" />
              <div className="lg:h-12 flex items-center justify-center px-3 gap-2">
                <div className="w-[72px] h-0.5 bg-blue-30 lg:hidden" />
                <span className="text-base font-semibold text-gray-600 whitespace-nowrap">
                  OR
                </span>
                <div className="w-[72px] h-0.5 bg-blue-30 lg:hidden" />
              </div>
              {hasAnyErrorMessages && (
                <div className="hidden lg:block pt-1 min-h-6" />
              )}
            </div>

            {/* Location Field */}
            <LocationField
              onLocationSelected={handleLocationChange}
              disabled={fieldsDisabled}
            />

            {/* Name Field */}
            <NameField
              locationCity={locationCityValue}
              onProviderUpdate={handleNameUpdatedProvider}
              disabled={fieldsDisabled}
            />

            {/* Last 4 Field */}
            <Last4Field
              disabled={disableVerificationFields}
              pwid={providerCode}
            />

            {/* Email Field */}
            <EmailField
              disabled={disableVerificationFields}
              existingProviders={existingProviders}
              providerEmail={foundProvider?.email ?? foundProvider?.Email ?? ''}
            />
          </div>
        </div>
      </FormProvider>

      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <DevTool control={control} />
        </Suspense>
      )}

      {/* Provider card with validation issues and Add button */}
      {foundProvider && (
        <ProviderSearchResult
          provider={foundProvider}
          blockerMessage={blockerMessage}
          canAdd={canAdd}
          onAdd={handleAdd}
          disabled={fieldsDisabled}
        />
      )}
    </div>
  );
};

ProviderSearch.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  existingProviders: PropTypes.arrayOf(PropTypes.object),
  existingRosterProviders: PropTypes.arrayOf(PropTypes.object),
  onSearchFieldsChange: PropTypes.func,
};

export default ProviderSearch;
